<script setup lang="ts">
import BulkActionBarWithActions from './BulkActionBarWithActions.vue';
import BulkSelectionBar from './BulkSelectionBar.vue';
import type { BulkBarScheduleSelection } from './bulkSelectionText';
import type { BulkSelectionMode, BulkSelectionPayload } from '@/composables/useBulkSelection';
import type { MixedRunKind } from '@/composables/useMixedBulkRun';

/**
 * The bar above the search results. The search page mounts it on every bulk-selectable entity.
 *
 * An entity with bulk actions gets `BulkActionBarWithActions`: the selection, the actions and the
 * run. An entity without them passes `withActions` false and gets `BulkSelectionBar` alone: the
 * count, the clear button and "Select all N matching".
 *
 * The split is what keeps the run off every other page. The run store is global, and following a
 * run fetches the active run, polls it, and may start a custom list run that waits for it. So only
 * the bar with actions creates the run tracker. A music zone search never asks for capabilities
 * or the active run, and never starts a waiting run whose outcome it could not show.
 */
const props = withDefaults(defineProps<{
  entitySlug: string;
  /** False on an entity with no bulk actions: no capabilities request, no actions and no run. */
  withActions?: boolean;
  /** False while the page wants the selection kept out of the bar, such as after a plain click. */
  showSelection?: boolean;
  mode: BulkSelectionMode;
  count: number;
  /** Rows unticked after "select all matching". */
  excludedCount?: number;
  /** The pager's total for the search on screen. */
  total?: number;
  /** Whether "Select all N matching" would add rows to the selection. */
  canSelectAllMatching?: boolean;
  /** True while the search page is fetching. The total may be a transient 0 then. */
  loading: boolean;
  /** Builds the request's `selection` at the moment an action starts. */
  buildSelection: () => BulkSelectionPayload | null;
  /**
   * The schedule selection, on the custom list search only. The list props above describe the
   * lists then. Null on every other entity, which keeps "Select all N matching".
   */
  schedules?: BulkBarScheduleSelection | null;
  /** Builds the schedule selection's `selection`, on the custom list search only. */
  buildScheduleSelection?: (() => BulkSelectionPayload | null) | null;
}>(), { withActions: true, showSelection: true, excludedCount: 0, total: 0, canSelectAllMatching: false, schedules: null, buildScheduleSelection: null });

const emit = defineEmits<{
  /**
   * Clear the selection. After an action starts it names the kinds the action covered, so the
   * page clears those and keeps the rest. For example, Add tag with lists and schedules ticked
   * clears the lists only. The clear button, and every entity without schedules, name none.
   */
  (e: 'clear', kinds?: MixedRunKind[]): void;
  (e: 'select-all-matching'): void;
  /** "112 schedules" was clicked: select every schedule that passes the search. */
  (e: 'select-all-matching-schedules'): void;
  /** The tracked run is final. Counts the page holds for the search may now be stale. */
  (e: 'run-finished'): void;
}>();
</script>

<template>
  <BulkActionBarWithActions
    v-if="props.withActions"
    :entity-slug="entitySlug"
    :show-selection="showSelection"
    :mode="mode"
    :count="count"
    :excluded-count="excludedCount"
    :total="total"
    :can-select-all-matching="canSelectAllMatching"
    :loading="loading"
    :build-selection="buildSelection"
    :schedules="schedules"
    :build-schedule-selection="buildScheduleSelection"
    @clear="(kinds) => emit('clear', kinds)"
    @select-all-matching="emit('select-all-matching')"
    @select-all-matching-schedules="emit('select-all-matching-schedules')"
    @run-finished="emit('run-finished')"
  />
  <BulkSelectionBar
    v-else
    :show-selection="showSelection"
    :mode="mode"
    :count="count"
    :excluded-count="excludedCount"
    :total="total"
    :can-select-all-matching="canSelectAllMatching"
    :schedules="schedules"
    @clear="emit('clear')"
    @select-all-matching="emit('select-all-matching')"
    @select-all-matching-schedules="emit('select-all-matching-schedules')"
  />
</template>
